import { QueryClient } from "@tanstack/react-query";

import { VideoProject, VideoProjectQueueSearchParams, VideoProjectServices } from "@/services/bedrock";

import { getKanbanColumnId, KANBAN_COLUMNS, KanbanColumnDef, KanbanColumnId } from "./kanbanColumns";
import { insertProjectInKanbanQueueOrder, isPriorityFor } from "./kanbanQueueOrder";

const KANBAN_COLUMN_PAGE_SIZE = 10;
export const KANBAN_EXTRA_FIELDS = {
  needsAck: true,
  isPriority: true,
  queueAssignments: ["staffUser"],
} as const;

export type KanbanBoardFilters = Pick<
  VideoProjectQueueSearchParams,
  "searchTerm" | "includeDemoProjects" | "assignees" | "priority"
>;

interface KanbanColumnSort {
  sort: string;
  order: "asc" | "desc";
}

export interface KanbanColumnFetchResult {
  projects: VideoProject[];
  totalCount: number;
  totalPriorityCount: number;
  nextCursor: string | null;
  hasMore: boolean;
  sort: KanbanColumnSort;
}

export type KanbanBoardData = Record<KanbanColumnId, KanbanColumnFetchResult>;

export function mediaUrlIdentity(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, "https://kanban-media.local");
    const protocol = parsed.protocol.toLowerCase();
    const hostname = parsed.hostname.toLowerCase();
    const isRelative = parsed.origin === "https://kanban-media.local";
    if (isRelative) return decodeURI(parsed.pathname);

    return `${protocol}//${hostname}${decodeURI(parsed.pathname)}`;
  } catch {
    const [withoutHash] = url.split("#");
    return withoutHash.split("?")[0];
  }
}

function queueAssignmentSignature(project: VideoProject): string {
  return (project.queueAssignments ?? [])
    .map(assignment => `${assignment.role}:${assignment.staffUserId}`)
    .sort()
    .join(",");
}

function kanbanProjectCardKey(project: VideoProject): string {
  return [
    project.id,
    project.status,
    project.statusLabel ?? "",
    project.isPriority ? "1" : "0",
    project.isTestProject ? "1" : "0",
    project.needsAck ? "1" : "0",
    project.editorUserId ?? "",
    project.qaUserId ?? "",
    project.qaStatus ?? "",
    project.playerName ?? "",
    project.companyName ?? "",
    project.revisionCount ?? 0,
    project.requiresQa ? "1" : "0",
    mediaUrlIdentity(project.thumbnailUrl),
    project.createdDate ?? "",
    queueAssignmentSignature(project),
  ].join("|");
}

function reuseVideoProject(previous: VideoProject | undefined, next: VideoProject): VideoProject {
  if (!previous || previous === next) return previous ?? next;
  if (previous.id !== next.id) return next;
  if (kanbanProjectCardKey(previous) === kanbanProjectCardKey(next)) {
    return previous;
  }
  if (mediaUrlIdentity(previous.thumbnailUrl) === mediaUrlIdentity(next.thumbnailUrl) && previous.thumbnailUrl) {
    return { ...next, thumbnailUrl: previous.thumbnailUrl };
  }
  return next;
}

export function reuseKanbanProjectList(previous: VideoProject[] | undefined, next: VideoProject[]): VideoProject[] {
  if (!previous) return next;
  if (previous === next) return previous;

  const previousById = new Map(previous.map(project => [project.id, project]));
  let sameOrderAndRefs = previous.length === next.length;
  const merged = next.map((project, index) => {
    const reused = reuseVideoProject(previousById.get(project.id), project);
    if (sameOrderAndRefs && previous[index] !== reused) sameOrderAndRefs = false;
    return reused;
  });
  return sameOrderAndRefs ? previous : merged;
}

export function reuseKanbanBoardData(previous: KanbanBoardData | undefined, next: KanbanBoardData): KanbanBoardData {
  if (!previous) return next;

  let changed = false;
  const result = { ...next };
  for (const column of KANBAN_COLUMNS) {
    const previousColumn = previous[column.id];
    const nextColumn = next[column.id];
    if (!nextColumn) {
      if (previousColumn) changed = true;
      continue;
    }
    if (!previousColumn) {
      changed = true;
      continue;
    }

    const projects = reuseKanbanProjectList(previousColumn.projects, nextColumn.projects);
    if (
      projects === previousColumn.projects &&
      previousColumn.totalCount === nextColumn.totalCount &&
      previousColumn.totalPriorityCount === nextColumn.totalPriorityCount &&
      previousColumn.nextCursor === nextColumn.nextCursor &&
      previousColumn.hasMore === nextColumn.hasMore &&
      previousColumn.sort.sort === nextColumn.sort.sort &&
      previousColumn.sort.order === nextColumn.sort.order
    ) {
      result[column.id] = previousColumn;
      continue;
    }

    result[column.id] = projects === nextColumn.projects ? nextColumn : { ...nextColumn, projects };
    changed = true;
  }

  return changed ? result : previous;
}

export function kanbanBoardQueryKey(filters: KanbanBoardFilters) {
  return ["videoProjects", "kanbanBoard", filters] as const;
}

const KANBAN_COLUMN_SORT: KanbanColumnSort = { sort: "queue_priority", order: "asc" };

export function isKanbanColumnFetchAborted(error: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true;
  if (error == null || typeof error !== "object") return false;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  const maybe = error as { name?: string; code?: string; message?: string };
  return (
    maybe.name === "AbortError" ||
    maybe.name === "CanceledError" ||
    maybe.code === "ERR_CANCELED" ||
    maybe.message === "canceled"
  );
}

async function fetchKanbanColumnSlice(
  column: KanbanColumnDef,
  filters: KanbanBoardFilters,
  options: { cursor?: string; perPage: number; includeTotal?: boolean },
  signal?: AbortSignal
): Promise<KanbanColumnFetchResult> {
  const sort = KANBAN_COLUMN_SORT;
  const response = await VideoProjectServices.index(
    {
      ...filters,
      status: column.statuses,
      perPage: options.perPage,
      sort: sort.sort,
      order: sort.order,
      cursor: options.cursor ?? "",
      extraFields: KANBAN_EXTRA_FIELDS,
      ...(options.includeTotal ? { includeTotal: true } : {}),
    },
    signal ? { signal } : undefined
  );

  return {
    projects: response.content,
    totalCount: response.pagination.totalCount ?? 0,
    totalPriorityCount: response.pagination.totalPriorityCount ?? 0,
    nextCursor: response.pagination.nextCursor ?? null,
    hasMore: Boolean(response.pagination.hasMore ?? response.pagination.nextCursor),
    sort,
  };
}

export async function fetchNextKanbanColumnPage(
  column: KanbanColumnDef,
  filters: KanbanBoardFilters,
  columnData: KanbanColumnFetchResult,
  signal?: AbortSignal
): Promise<KanbanColumnFetchResult> {
  return fetchKanbanColumnSlice(
    column,
    filters,
    { cursor: columnData.nextCursor ?? "", perPage: KANBAN_COLUMN_PAGE_SIZE },
    signal
  );
}

export async function fetchKanbanBoard(
  filters: KanbanBoardFilters,
  options?: { signal?: AbortSignal }
): Promise<KanbanBoardData> {
  const columnResults = await Promise.all(
    KANBAN_COLUMNS.map(async column => {
      const result = await fetchKanbanColumnSlice(
        column,
        filters,
        { cursor: "", perPage: KANBAN_COLUMN_PAGE_SIZE, includeTotal: true },
        options?.signal
      );
      return [column.id, result] as const;
    })
  );

  return Object.fromEntries(columnResults) as KanbanBoardData;
}

export async function refreshKanbanColumns(
  filters: KanbanBoardFilters,
  boardData: KanbanBoardData,
  columnIds: KanbanColumnId[],
  signal?: AbortSignal
): Promise<KanbanBoardData> {
  const uniqueColumnIds = [...new Set(columnIds)];
  const refreshedColumns = await Promise.all(
    uniqueColumnIds.map(async columnId => {
      const column = KANBAN_COLUMNS.find(item => item.id === columnId);
      const columnData = boardData[columnId];
      if (!column || !columnData) return null;

      const perPage = Math.max(KANBAN_COLUMN_PAGE_SIZE, columnData.projects.length);
      const refreshed = await fetchKanbanColumnSlice(
        column,
        filters,
        { cursor: "", perPage, includeTotal: true },
        signal
      );
      return [columnId, refreshed] as const;
    })
  );

  const updates = Object.fromEntries(
    refreshedColumns.filter((entry): entry is readonly [KanbanColumnId, KanbanColumnFetchResult] => entry != null)
  );

  return reuseKanbanBoardData(boardData, { ...boardData, ...updates });
}

export function appendKanbanColumnPage(
  boardData: KanbanBoardData,
  columnId: KanbanColumnId,
  page: Pick<KanbanColumnFetchResult, "projects" | "nextCursor" | "hasMore"> & {
    totalCount?: number;
    totalPriorityCount?: number;
  }
): KanbanBoardData {
  const columnData = boardData[columnId];
  if (!columnData) return boardData;

  const existingIds = new Set(columnData.projects.map(project => project.id));
  const newProjects = page.projects.filter(project => !existingIds.has(project.id));
  const totalCount = page.totalCount ?? columnData.totalCount;
  const totalPriorityCount = page.totalPriorityCount ?? columnData.totalPriorityCount;
  if (
    newProjects.length === 0 &&
    totalCount === columnData.totalCount &&
    totalPriorityCount === columnData.totalPriorityCount &&
    page.nextCursor === columnData.nextCursor &&
    page.hasMore === columnData.hasMore
  ) {
    return boardData;
  }

  return {
    ...boardData,
    [columnId]: {
      ...columnData,
      projects: [...columnData.projects, ...newProjects],
      totalCount,
      totalPriorityCount,
      nextCursor: page.nextCursor,
      hasMore: page.hasMore,
    },
  };
}

export function moveProjectInKanbanBoardData(
  boardData: KanbanBoardData,
  projectId: number,
  fromColumnId: KanbanColumnId,
  toColumnId: KanbanColumnId,
  patch: Partial<VideoProject>,
  currentUserId: number
): KanbanBoardData {
  const fromColumn = boardData[fromColumnId];
  const toColumn = boardData[toColumnId];
  if (!fromColumn || !toColumn) return boardData;

  const project = fromColumn.projects.find(item => item.id === projectId);
  if (!project) return boardData;

  const updatedProject = {
    ...project,
    ...patch,
    isPriority: isPriorityFor({ ...project, ...patch }, currentUserId),
  };
  const wasPriority = countsTowardPriorityTotal(fromColumnId, Boolean(project.isPriority));
  const isPriority = countsTowardPriorityTotal(toColumnId, Boolean(updatedProject.isPriority));

  if (fromColumnId === toColumnId) {
    return {
      ...boardData,
      [fromColumnId]: withColumnCounts(
        {
          ...fromColumn,
          projects: insertProjectInKanbanQueueOrder(
            fromColumn.projects.map(item => (item.id === projectId ? updatedProject : item)),
            updatedProject,
            fromColumnId,
            currentUserId
          ),
        },
        { priority: Number(isPriority) - Number(wasPriority) }
      ),
    };
  }

  const alreadyInDestination = toColumn.projects.some(item => item.id === projectId);

  return {
    ...boardData,
    [fromColumnId]: withColumnCounts(
      {
        ...fromColumn,
        projects: fromColumn.projects.filter(item => item.id !== projectId),
      },
      { total: -1, priority: wasPriority ? -1 : 0 }
    ),
    [toColumnId]: withColumnCounts(
      {
        ...toColumn,
        projects: insertProjectInKanbanQueueOrder(toColumn.projects, updatedProject, toColumnId, currentUserId),
      },
      {
        total: alreadyInDestination ? 0 : 1,
        priority: alreadyInDestination ? 0 : isPriority ? 1 : 0,
      }
    ),
  };
}

export function findKanbanProjectColumnId(boardData: KanbanBoardData, projectId: number): KanbanColumnId | null {
  for (const column of KANBAN_COLUMNS) {
    if (boardData[column.id]?.projects.some(item => item.id === projectId)) {
      return column.id;
    }
  }
  return null;
}

export function columnIdForStatus(status?: VideoProject.Status | string | null): KanbanColumnId | null {
  if (!status) return null;
  return getKanbanColumnId(status as VideoProject.Status) ?? null;
}

export function projectMatchesKanbanFilters(project: VideoProject, filters: KanbanBoardFilters): boolean {
  if (filters.includeDemoProjects === false && project.isTestProject) {
    return false;
  }

  if (filters.priority === true && !project.isPriority) {
    return false;
  }

  if (filters.assignees && filters.assignees.length > 0) {
    const editorId = project.editorUserId ?? null;
    if (!filters.assignees.some(assigneeId => assigneeId === editorId)) {
      return false;
    }
  }

  const term = filters.searchTerm?.trim();
  if (!term) return true;

  const needle = term.toLowerCase();
  return String(project.id) === term || Boolean(project.playerName?.toLowerCase().includes(needle));
}

function countsTowardPriorityTotal(columnId: KanbanColumnId, isPriority: boolean): boolean {
  return isPriority && columnId !== "completed";
}

function withColumnCounts(
  column: KanbanColumnFetchResult,
  deltas: { total?: number; priority?: number }
): KanbanColumnFetchResult {
  const totalCount = Math.max(0, column.totalCount + (deltas.total ?? 0));
  const totalPriorityCount = Math.max(0, column.totalPriorityCount + (deltas.priority ?? 0));
  if (totalCount === column.totalCount && totalPriorityCount === column.totalPriorityCount) {
    return column;
  }
  return { ...column, totalCount, totalPriorityCount };
}

export function removeProjectFromKanbanBoardData(
  boardData: KanbanBoardData,
  projectId: number,
  fallbackColumnId?: KanbanColumnId | null
): KanbanBoardData {
  const columnId = findKanbanProjectColumnId(boardData, projectId) ?? fallbackColumnId ?? null;
  if (!columnId) return boardData;

  const column = boardData[columnId];
  if (!column) return boardData;

  const removed = column.projects.find(item => item.id === projectId);
  const projects = removed ? column.projects.filter(item => item.id !== projectId) : column.projects;
  const nextColumn = withColumnCounts(projects === column.projects ? column : { ...column, projects }, {
    total: -1,
    priority: countsTowardPriorityTotal(columnId, Boolean(removed?.isPriority)) ? -1 : 0,
  });
  if (nextColumn === column) {
    return boardData;
  }

  return {
    ...boardData,
    [columnId]: nextColumn,
  };
}

export function updateProjectInKanbanBoardData(
  boardData: KanbanBoardData,
  projectId: number,
  patch: Partial<VideoProject>,
  currentUserId: number
): KanbanBoardData {
  const columnId = findKanbanProjectColumnId(boardData, projectId);
  if (!columnId) return boardData;

  const project = boardData[columnId].projects.find(item => item.id === projectId);
  if (!project) return boardData;

  const toColumnId = getKanbanColumnId(patch.status ?? project.status) ?? columnId;
  return moveProjectInKanbanBoardData(boardData, projectId, columnId, toColumnId, patch, currentUserId);
}

function insertProjectIntoKanbanColumn(
  boardData: KanbanBoardData,
  columnId: KanbanColumnId,
  project: VideoProject,
  currentUserId: number,
  options?: { incrementTotal?: boolean }
): KanbanBoardData {
  const column = boardData[columnId];
  if (!column) return boardData;

  const alreadyLoaded = column.projects.some(item => item.id === project.id);
  const incrementTotal = options?.incrementTotal ?? !alreadyLoaded;

  return {
    ...boardData,
    [columnId]: withColumnCounts(
      {
        ...column,
        projects: insertProjectInKanbanQueueOrder(column.projects, project, columnId, currentUserId),
      },
      {
        total: incrementTotal ? 1 : 0,
        priority: incrementTotal && countsTowardPriorityTotal(columnId, Boolean(project.isPriority)) ? 1 : 0,
      }
    ),
  };
}

interface ApplyKanbanVideoProjectOptions {
  filters?: KanbanBoardFilters;
  previousStatus?: VideoProject.Status | string | null;
}

function shouldIncrementTotalsForInsertedProject(
  previousColumnId: KanbanColumnId | null,
  toColumnId: KanbanColumnId,
  filters?: KanbanBoardFilters
): boolean {
  if (previousColumnId !== toColumnId) return true;

  // Same-column inserts are already included in the unfiltered column total. Restrictive
  // filters use a smaller total, so a project that just started matching must increment.
  return filters?.priority === true || Boolean(filters?.assignees?.length);
}

export function applyKanbanVideoProject(
  boardData: KanbanBoardData,
  project: VideoProject,
  currentUserId: number,
  options: ApplyKanbanVideoProjectOptions = {}
): KanbanBoardData {
  const fromColumnId = findKanbanProjectColumnId(boardData, project.id);
  const toColumnId = getKanbanColumnId(project.status) ?? null;
  const previousColumnId = columnIdForStatus(options.previousStatus);
  const matchesFilters = !options.filters || projectMatchesKanbanFilters(project, options.filters);

  if (!matchesFilters || !toColumnId) {
    return fromColumnId ? removeProjectFromKanbanBoardData(boardData, project.id) : boardData;
  }

  if (!fromColumnId) {
    let nextBoard = boardData;
    if (previousColumnId && previousColumnId !== toColumnId && nextBoard[previousColumnId]) {
      const previousStatus = options.previousStatus as VideoProject.Status | undefined;
      const wasPriority = previousStatus
        ? countsTowardPriorityTotal(
            previousColumnId,
            isPriorityFor({ ...project, status: previousStatus }, currentUserId)
          )
        : false;
      nextBoard = {
        ...nextBoard,
        [previousColumnId]: withColumnCounts(nextBoard[previousColumnId], {
          total: -1,
          priority: wasPriority ? -1 : 0,
        }),
      };
    }

    return insertProjectIntoKanbanColumn(nextBoard, toColumnId, project, currentUserId, {
      incrementTotal: shouldIncrementTotalsForInsertedProject(previousColumnId, toColumnId, options.filters),
    });
  }

  const existing = boardData[fromColumnId].projects.find(item => item.id === project.id);
  const nextProject = reuseVideoProject(existing, project);
  if (nextProject === existing && fromColumnId === toColumnId) {
    return boardData;
  }

  return moveProjectInKanbanBoardData(boardData, project.id, fromColumnId, toColumnId, nextProject, currentUserId);
}

export async function fetchKanbanVideoProject(projectId: number, signal?: AbortSignal): Promise<VideoProject> {
  return VideoProjectServices.show(projectId, KANBAN_EXTRA_FIELDS, signal ? { signal } : undefined);
}

export function setKanbanBoardQueryData(
  queryClient: QueryClient,
  filters: KanbanBoardFilters,
  boardData: KanbanBoardData
): KanbanBoardData | undefined {
  return queryClient.setQueryData<KanbanBoardData>(kanbanBoardQueryKey(filters), boardData);
}
